/**
 * StartTimePreferenceSelector - mode-aware start time preference with live preview
 *
 * Derives the booking mode from selectedTimeSlot + isSimultaneous, then calls
 * getStartTimeOptions() (single source of truth in @sda/shared) to build the
 * radio list.  Only options present in the mode's table are rendered — late is
 * automatically absent for back_to_back / simultaneous / second_slot without
 * any manual filtering here.
 *
 * The preview reflects the mode: second_slot shows only Slot 2 (11:00/11:30),
 * single shows only Slot 1, both-modes show both slots with the simultaneous
 * tag when applicable.
 */

import { useEffect, useMemo } from 'react';
import type { PublicSystemSettings, TimeSlotSelection } from '../../api/events';
import {
  resolveSessionTimes,
  getStartTimeOptions,
  formatTimeDisplay,
  type StartTimePreference,
  type SessionMode,
} from '@sda/shared';

interface StartTimePreferenceSelectorProps {
  systemSettings: PublicSystemSettings;
  matineeTime: string | null;
  preference: StartTimePreference;
  onPreferenceChange: (pref: StartTimePreference) => void;
  isSimultaneous: boolean;
  selectedTimeSlot?: TimeSlotSelection;
}

/** Derive SessionMode from the selected time slot and simultaneous flag. */
function deriveMode(selectedTimeSlot: TimeSlotSelection | undefined, isSimultaneous: boolean): SessionMode {
  if (selectedTimeSlot === 'both') {
    return isSimultaneous ? 'simultaneous' : 'back_to_back';
  }
  if (selectedTimeSlot === '11:00 AM') return 'second_slot';
  // '9:00 AM' or undefined → single
  return 'single';
}

export function StartTimePreferenceSelector({
  systemSettings,
  matineeTime,
  preference,
  onPreferenceChange,
  isSimultaneous,
  selectedTimeSlot,
}: StartTimePreferenceSelectorProps) {
  const config = useMemo(() => ({
    defaultSessionStartTime: systemSettings.default_session_start_time,
    matineeTime: matineeTime,
    sessionDurationMinutes: systemSettings.session_duration_minutes,
    matineeThresholdTime: systemSettings.matinee_threshold_time,
    defaultMatineeTime: systemSettings.default_matinee_time,
  }), [systemSettings, matineeTime]);

  const mode = useMemo(
    () => deriveMode(selectedTimeSlot, isSimultaneous),
    [selectedTimeSlot, isSimultaneous],
  );

  // Options + default preference from the single source of truth in @sda/shared.
  // Each option already has the resolved startTime for display.
  const { options, defaultPreference } = useMemo(
    () => getStartTimeOptions(config, mode),
    [config, mode],
  );

  // When the mode changes and the current preference is no longer in the new
  // option set, reset to the mode's defaultPreference.
  useEffect(() => {
    const validPreferences = options.map((o) => o.preference);
    if (!validPreferences.includes(preference)) {
      onPreferenceChange(defaultPreference);
    }
  }, [options, preference, onPreferenceChange, defaultPreference]);

  // Resolved times for the live preview (driven by current preference + mode).
  const resolved = useMemo(
    () => resolveSessionTimes(config, preference, mode),
    [config, preference, mode],
  );

  // Which session(s) to show in the preview.
  const showSession = selectedTimeSlot === '11:00 AM' ? 'session2'
    : selectedTimeSlot === '9:00 AM' ? 'session1'
    : 'both';

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-warm-900 mb-4">
        When would you like your class to start?
      </h3>

      <div className="space-y-2">
        {options.map((option) => {
          const startDisplay = formatTimeDisplay(option.startTime);
          const isSelected = preference === option.preference;
          const isDefault = option.preference === defaultPreference;

          return (
            <button
              key={option.preference}
              type="button"
              onClick={() => onPreferenceChange(option.preference)}
              className={`
                w-full p-3 rounded-lg border-2 text-left transition-all
                ${isSelected
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-200'
                  : 'border-warm-200 hover:border-primary-300 hover:bg-warm-50'
                }
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? 'border-primary-500' : 'border-warm-400'
                  }`}>
                    {isSelected && <div className="w-2 h-2 rounded-full bg-primary-500" />}
                  </div>
                  <div>
                    <span className="font-medium text-warm-900">{startDisplay}</span>
                    {isDefault && (
                      <span className="text-warm-500 ml-2 text-sm">&mdash; (recommended)</span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Confirmation note for non-default preference */}
      {preference !== 'default' && (
        <p className="mt-3 text-sm text-amber-600">
          Start time preference noted. Our team will confirm your class times after booking.
        </p>
      )}

      {/* Live session time preview */}
      <div className="mt-4 p-3 bg-warm-50 rounded-lg">
        <div className="text-xs font-medium text-warm-500 uppercase tracking-wide mb-2">Class Times Preview</div>
        <div className="space-y-1 text-sm">
          {showSession !== 'session2' && (
            <div className="flex justify-between">
              <span className="text-warm-600">Class Slot 1</span>
              <span className="font-medium text-warm-900">
                {formatTimeDisplay(resolved.session1.start)} &ndash; {formatTimeDisplay(resolved.session1.end)}
              </span>
            </div>
          )}
          {showSession !== 'session1' && (
            <div className="flex justify-between">
              <span className="text-warm-600">Class Slot 2</span>
              <span className="font-medium text-warm-900">
                {formatTimeDisplay(resolved.session2.start)} &ndash; {formatTimeDisplay(resolved.session2.end)}
                {resolved.isSimultaneous && (
                  <span className="text-amber-600 ml-1 text-xs">(simultaneous)</span>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StartTimePreferenceSelector;
